import { FatGreenOutlineButton } from "common";
import { Pane } from "evergreen-ui";
import { Offer } from "offer";
import { useGoToReferralListPage } from "providerapp/core/routes";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Referral, SelectedReferral } from "referral";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import {
  map,
  distinctUntilChanged,
  filter,
  debounceTime,
} from "rxjs/operators";
import { ErrorDialog } from "../../common/component/ErrorDialog";
import { useSubmitNewOffer$ } from "../hook/useSubmitNewOffer$";
import { OfferForm, OfferFormInput } from "./OfferForm";
import { OfferFormHeader } from "./OfferFormHeader";
import {
  SiblingOfferSuggestionDialog,
  useSiblingOfferSuggestionDialog,
} from "./siblings/SiblingOfferSuggestionDialog";
import { HigherPrioritySuggestionDialog } from "./HigherPrioritySuggestionDialog";
import { useHigherPrioritySuggestion$ } from "../hook/useHigherPrioritySuggestion$";
import { useObservable } from "rxsupport/hook/useObservable";

type NewOfferFormProps = {
  referral: Referral;
  successCallback: (newOffer: Partial<Offer>) => any;
};
export const NewOfferForm = memo(
  ({ referral, successCallback }: NewOfferFormProps) => {
    const {
      showSuggestedReferralDecisionForm$,
      confirmSuggestedReferralDecision$,
      suggestedReferralDecision,
      isEnabled: isHigherPrioritySuggestionEnabled,
      triggerSuggestionFromValidation,
      canSubmitOffer$,
      handleOfferSubmitted,
      prepareForSubmission,
      suggestedReferralGuid,
    } = useHigherPrioritySuggestion$(referral.guid);

    const {
      offerSubmissionResult$,
      showSiblingOfferSuggestion$,
      confirmSiblingOfferSuggestion$,
    } = useSiblingOfferSuggestionDialog(referral, successCallback);

    const onOfferSubmitted = useCallback(
      (offer: Partial<Offer>) => {
        const shouldRedirect = handleOfferSubmitted(suggestedReferralDecision);

        if (!shouldRedirect) {
          // Continue with normal flow
          if (
            SelectedReferral.filterSiblingReferralsForOffer(referral).length ===
            0
          ) {
            successCallback(offer);
          } else {
            offerSubmissionResult$.next(offer);
            showSiblingOfferSuggestion$.next(true);
          }
        }
        // If shouldRedirect is true, the hook handles the redirect internally
      },
      [
        offerSubmissionResult$,
        referral,
        showSiblingOfferSuggestion$,
        successCallback,
        suggestedReferralDecision,
        handleOfferSubmitted,
      ],
    );

    const {
      submitOffer,
      isRunning$: isSubmitting$,
      errorMessage$,
    } = useSubmitNewOffer$(referral.guid, onOfferSubmitted);

    const [shouldValidateSignal$] = useState(
      () => new BehaviorSubject<boolean>(false),
    );
    const [validationResults$] = useState(
      () => new BehaviorSubject<boolean>(false),
    );

    useEffect(() => {
      // Only trigger suggestion flow if feature is enabled
      if (!isHigherPrioritySuggestionEnabled) {
        return;
      }

      const sub = validationResults$
        .pipe(filter((isValid) => isValid))
        .subscribe(() => {
          triggerSuggestionFromValidation();
        });
      return () => sub.unsubscribe();
    }, [
      validationResults$,
      triggerSuggestionFromValidation,
      isHigherPrioritySuggestionEnabled,
    ]);

    const submitSignal$ = useMemo<Observable<boolean>>(
      () =>
        combineLatest([
          shouldValidateSignal$,
          validationResults$,
          canSubmitOffer$,
        ]).pipe(
          map(([shouldValidate, validationResults, canSubmit]) => {
            return (shouldValidate || validationResults) && canSubmit;
          }),
          distinctUntilChanged(),
          debounceTime(500),
        ),
      [shouldValidateSignal$, validationResults$, canSubmitOffer$],
    );

    const goToReferralListPage = useGoToReferralListPage();

    const submitOperation = useCallback(
      (offer: OfferFormInput) => {
        if (prepareForSubmission()) {
          const input =
            suggestedReferralDecision == null
              ? null
              : {
                  ...suggestedReferralDecision,
                  suggestedReferralGuid,
                };
          submitOffer(offer, input);
        }
      },
      [
        submitOffer,
        suggestedReferralDecision,
        prepareForSubmission,
        suggestedReferralGuid,
      ],
    );

    const isSubmitting = useObservable(isSubmitting$, false);

    return (
      <Pane className="max-h-full h-full box-border portal-provider-offer-form-container flex flex-col overflow-y-scroll">
        <OfferFormHeader
          caseNumber={referral.caseNumber}
          onClose={goToReferralListPage}
        />

        <OfferForm
          referral={referral}
          validatePlease$={shouldValidateSignal$ as Observable<boolean>}
          validationResult$={validationResults$}
          submitPlease$={submitSignal$}
          isSubmitting$={isSubmitting$}
          submitOperation={submitOperation}
        />

        <Pane className="portal-provider-offer-form-actions portal-provider-bg flex justify-evenly">
          <Pane width={200}>
            <FatGreenOutlineButton
              text="Cancel"
              type="button"
              onClick={goToReferralListPage}
              disabled={isSubmitting}
            />
          </Pane>
          <Pane width={200}>
            <FatGreenOutlineButton
              text="Submit Offer"
              type="button"
              onClick={() => shouldValidateSignal$.next(true)}
              disabled={isSubmitting}
            />
          </Pane>
        </Pane>

        <ErrorDialog message$={errorMessage$} />

        <SiblingOfferSuggestionDialog
          show$={showSiblingOfferSuggestion$}
          confirm$={confirmSiblingOfferSuggestion$}
        />

        {isHigherPrioritySuggestionEnabled && (
          <HigherPrioritySuggestionDialog
            show$={showSuggestedReferralDecisionForm$}
            confirm$={confirmSuggestedReferralDecision$}
          />
        )}
      </Pane>
    );
  },
);
